'use client';

import React, { useCallback, useRef } from 'react';
import { InquiryFlow, type FlowDocument, type RecordedVideoValue } from '@miso/design-system';
import {
  requestUploadToken,
  requestResumableUpload,
  uploadToPresignedUrl,
  uploadResumableBlob,
} from '@/entities/unstructured';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';

// ── FlowDocument 상수 ───────────────────────────────────────────────────────

const COVER_STEP_ID = 'sketch-cover';
const VIDEO_STEP_ID = 'sketch-video';
const TERMINAL_ID = 'sketch-done';
const VIDEO_QUESTION_ID = 'sketchVideo';

/**
 * 집 스케치 영상 촬영 플로우 — cover → video-full 두 단계.
 *
 * - cover: 이모지 + 타이틀 + "촬영하기" CTA (입력 없음)
 * - video-full: 전체 화면 카메라 녹화 (필수)
 */
const SKETCH_FLOW: FlowDocument = {
  schemaVersion: 1,
  id: 'sketch-video-flow',
  initial: COVER_STEP_ID,
  steps: {
    [COVER_STEP_ID]: {
      kind: 'questions',
      questions: [
        {
          id: 'sketch-cover-q',
          layout: 'cover',
          emoji: '🏠',
          title: '집 스케치',
          description: '집 스케치 영상 촬영하기',
          ctaLabel: '촬영하기',
          input: { kind: 'none' },
        },
      ],
      next: { kind: 'goto', target: VIDEO_STEP_ID },
    },
    [VIDEO_STEP_ID]: {
      kind: 'questions',
      questions: [
        {
          id: VIDEO_QUESTION_ID,
          layout: 'video-full',
          appBarTitle: '집 스케치 영상',
          title: '집 스케치 영상',
          input: {
            kind: 'video-recorder',
            required: true,
            preferUltraWide: true,
            defaultFacingMode: 'environment',
            autoRequestPermission: true,
          },
        },
      ],
      next: { kind: 'terminal', target: TERMINAL_ID },
    },
  },
  terminals: {
    [TERMINAL_ID]: { kind: 'placeholder-auto' },
  },
};

// ── Props ────────────────────────────────────────────────────────────────────

export interface SketchVideoStepAdapterProps {
  visitCounselId: string;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 영상 업로드 + 세션 complete 성공 시 호출 — page 가 stage 재평가로 다음 단계 진입. */
  onAfterDone: (counsel: VisitCounselView) => void;
  /** 사용자 취소(뒤로가기) — page 가 fallback. */
  onCancel: () => void;
}

// ── 컴포넌트 ─────────────────────────────────────────────────────────────────

/**
 * 집 스케치 영상 촬영 어댑터 — `CounselStartPage` 의 `sketch-video` stage 에서 사용.
 *
 * `InquiryFlow` 기반:
 *   1. cover step  — 🏠 집 스케치 / "촬영하기" CTA
 *   2. video-full  — 전체 화면 카메라 녹화 (필수)
 *   3. handleComplete — blob 업로드 → `completeSketchingSession` → refetch 폴링 → `onAfterDone`
 *
 * `progress` 7-stage 완료 후 `CounselReviewScreen` 의 CTA 를 거쳐 진입한다.
 * sketching session 은 이미 STARTED 상태이며 본 어댑터가 영상 업로드 후
 * `completeSketchingSession` 을 호출해 sketching.video / status='COMPLETED' 로 전환한다.
 */
export function SketchVideoStepAdapter({
  visitCounselId,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
}: SketchVideoStepAdapterProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleComplete = useCallback(
    async (accumulated: Record<string, unknown>) => {
      const recordedVideo =
        (accumulated[VIDEO_QUESTION_ID] as RecordedVideoValue | null | undefined) ?? null;

      if (!recordedVideo?.blob) {
        showToast('영상 촬영이 필요합니다.', 'error');
        return;
      }

      try {
        const contentType = recordedVideo.mimeType || 'video/webm';
        // NEXT_PUBLIC_RESUMABLE_VIDEO=1 이면 resumable(청크) 업로드로 전환한다.
        // 실패 지점 재개 + (향후) 녹화 중 스트리밍의 토대. 미설정 시 기존 단일 PUT.
        const useResumable = process.env.NEXT_PUBLIC_RESUMABLE_VIDEO === '1';
        const resp = useResumable
          ? await requestResumableUpload({
              resourceType: 'VIDEO',
              subType: 'VISIT_COUNSEL_VIDEO_CLIP',
              contentType,
            })
          : await requestUploadToken({
              resourceType: 'VIDEO',
              subType: 'VISIT_COUNSEL_VIDEO_CLIP',
              contentType,
            });
        const { structuredToken, uploadUrl } = resp;

        try {
          // 백엔드가 RESUMABLE 로 응답할 때만 청크 업로드. 미지원(SINGLE)이면 단일 PUT 폴백.
          if (resp.uploadMode === 'RESUMABLE') {
            await uploadResumableBlob(
              { sessionInitUrl: uploadUrl, contentType },
              recordedVideo.blob
            );
          } else {
            const file = new File([recordedVideo.blob], 'house-sketch-video.webm', {
              type: contentType,
            });
            await uploadToPresignedUrl(uploadUrl, file, contentType);
          }
        } catch (uploadErr) {
          const msg = uploadErr instanceof Error ? uploadErr.message : '영상 업로드 실패';
          showToast(`스토리지 업로드 실패: ${msg}`, 'error');
          return;
        }

        await myVisitCounselApi.completeSketchingSession(visitCounselId, {
          videoToken: structuredToken,
        });
        showToast('집 스케치 영상이 등록되었습니다', 'success');

        // backend 가 sketching.video 를 반영할 때까지 짧게 폴링
        const MAX_WAIT_MS = 10_000;
        const POLL_INTERVAL_MS = 400;
        const start = Date.now();
        let result = await refetch();
        const isReady = (c?: VisitCounselView | null): boolean => !!c?.sessions?.sketching?.video;
        while (!isReady(result?.counsel) && Date.now() - start < MAX_WAIT_MS) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          result = await refetch();
        }

        const next = result?.counsel ?? null;
        if (next) onAfterDone(next);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '영상 업로드에 실패했습니다', 'error');
      }
    },
    [visitCounselId, refetch, showToast, onAfterDone, onCancel]
  );

  return (
    <div ref={containerRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <InquiryFlow
        flow={SKETCH_FLOW}
        initialValues={{}}
        registries={{ terminals: {} }}
        onComplete={handleComplete}
        onCancel={onCancel}
        onError={(err) => {
          showToast(err.message ?? '오류가 발생했습니다', 'error');
          onCancel();
        }}
        container={containerRef.current}
      />
    </div>
  );
}

export default SketchVideoStepAdapter;
